// useEngagementArtifacts — item 14 (Asana 1216373781992284), the Deliverables tab.
//
// Fetches the live 4-phase / 14-artifact catalog from /api/engagement/artifacts. No demo
// fixtures once live data loads (same philosophy as useFrameworkData.ts): a phase's artifacts
// show real Ready/Draft/Queued/Planned states computed from this tenant's own data, never the
// frontend/src/components/LogicDecomposer/fixtures.ts placeholder set.

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../api';
import type { EngagementArtifact, PhaseDef } from './types';

const PHASE_TITLES: Record<number, string> = {
  1: 'Governance & Program Setup',
  2: 'Population Intelligence & Sequencing',
  3: 'Retirement Execution',
  4: 'Sustain, Transfer & Prevent Recurrence',
};

export interface UseEngagementArtifacts {
  phases: PhaseDef[];
  loading: boolean;
  source: 'empty' | 'live';
  reload: () => Promise<void>;
  // Fetches + downloads one artifact through the browser (auth'd via apiClient, so this can't be
  // a plain <a href> to the API route — see download() below for why).
  download: (artifact: EngagementArtifact) => Promise<{ url: string; filename: string }>;
}

export function useEngagementArtifacts(): UseEngagementArtifacts {
  const [phases, setPhases] = useState<PhaseDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<'empty' | 'live'>('empty');

  const reload = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/engagement/artifacts');
      const items: EngagementArtifact[] = Array.isArray(res?.data?.items) ? res.data.items : [];
      if (!items.length) { setPhases([]); setSource('empty'); return; }
      const byPhase = new Map<number, EngagementArtifact[]>();
      for (const a of items) {
        if (!byPhase.has(a.phase)) byPhase.set(a.phase, []);
        byPhase.get(a.phase)!.push(a);
      }
      const built: PhaseDef[] = [1, 2, 3, 4].map((id) => {
        const artifacts = byPhase.get(id as 1 | 2 | 3 | 4) || [];
        const readyCount = artifacts.filter((a) => a.state === 'Ready' || a.state === 'Draft').length;
        return {
          id: id as 1 | 2 | 3 | 4,
          title: PHASE_TITLES[id],
          // A real, computed statement — never a fabricated "Week N" timeline this app has no
          // actual project-schedule data to back.
          statusLine: readyCount === 0 ? 'Not yet generated' : `${readyCount} of ${artifacts.length} generated`,
          progress: readyCount > 0 ? 'active' : 'queued',
          artifacts,
        };
      });
      setPhases(built);
      setSource('live');
    } catch {
      setPhases([]); setSource('empty');
    } finally {
      setLoading(false);
    }
  }, []);

  // Blob-fetch + object URL, same pattern as useAttestation.ts's exportPack and
  // ArtifactPanel.tsx's downloadText — NOT a plain `window.location.href` to the API route,
  // because auth here goes through apiClient's Authorization header (api.ts), which a raw
  // navigation wouldn't carry.
  const download = useCallback(async (artifact: EngagementArtifact) => {
    const res = await apiClient.get(`/api/engagement/artifacts/${encodeURIComponent(artifact.id)}/download`, { responseType: 'blob' });
    const blob = res.data as Blob;
    const url = URL.createObjectURL(blob);
    const cd = res.headers?.['content-disposition'] as string | undefined;
    const match = cd && /filename="([^"]+)"/.exec(cd);
    const filename = match ? match[1] : artifact.name;
    return { url, filename };
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return { phases, loading, source, reload, download };
}
